/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ExecutionResult } from 'apollo-link'
import { Alert, Button, Cell, Grid, HFlow, Theme, Tooltip, useTheme } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { CpfField, Form, FormRenderProps, SelectField, SubmitButton, TextField } from 'components/form'
import { CnpjField } from 'components/form/field/CnpjField'
import { TipoPessoaGroupField } from 'components/form/field/TipoPessoaRadioGroupField'
import { FormApi } from 'final-form'
import { useSalvarSistemaExternoMutation } from 'graphql/hooks.generated'
import { SalvarSistemaExternoMutation, TipoPessoaEnum } from 'graphql/types.generated'
import { useState } from 'react'
import { isHttpsConnection } from 'util/https'
import { metaPath } from 'util/metaPath'
import { cnpj, cpf, createValidator, email, maxLength, minLength, nome, required, validate } from 'util/validation'

import { ChaveAssinatura, ChaveAssinaturaGeradaModal } from './ChaveAssinaturaGeradaModal'
import { SistemasExternosTermoModal } from './SistemasExternosTermoModal'

interface SistemaExternoFormModel {
  nome: string
  localAcesso: string
  url: string
  nomeResponsavel: string
  tipoPessoa: TipoPessoaEnum
  cpfCnpj: string
  email: string
}

const path = metaPath<SistemaExternoFormModel>()

const validator = createValidator<SistemaExternoFormModel>(
  {
    nome: [required, minLength(3), maxLength(100)],
    url: [required, minLength(12), maxLength(2048)],
    nomeResponsavel: [required, nome()],
    email: [required, email, minLength(6), maxLength(100)],
  },
  (values, errors) => {
    if (!errors.url && !values.url.toLowerCase().startsWith('https://')) {
      errors.url = 'A URL deve começar com https://'
      return errors
    }

    errors.tipoPessoa = validate(values.tipoPessoa, required)

    if (errors.tipoPessoa) return errors

    errors.cpfCnpj = validate(values.cpfCnpj, required)

    if (errors.cpfCnpj) return errors

    if (values.tipoPessoa === TipoPessoaEnum.FISICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cpf)
    }

    if (values.tipoPessoa === TipoPessoaEnum.JURIDICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cnpj)
    }

    return errors
  }
)

interface SistemasExternosFormProps {
  readonly refetch: () => void
}

export function SistemasExternosForm({ refetch }: SistemasExternosFormProps) {
  const isFormEnabled = isHttpsConnection
  const [salvarSistemaExterno] = useSalvarSistemaExternoMutation()
  const alert = useAlert()
  const handleRejection = useErrorHandler()

  const theme = useTheme()
  const styles = createStyles(theme)

  const [isModalChaveAssinaturaOpen, setIsModalChaveAssinaturaOpen] = useState(false)
  const [isModalTermoOpen, setIsModalTermoOpen] = useState(false)
  const [chaveAssinatura, setChaveAssinatura] = useState<ChaveAssinatura>()
  const [termoConfirmCallback, setTermoConfirmCallback] = useState<() => Promise<void>>()

  const onSubmitSuccess = (
    payload: ExecutionResult<SalvarSistemaExternoMutation>,
    form: FormApi<SistemaExternoFormModel>
  ) => {
    refetch()
    alert('success', 'Sistema externo cadastrado com sucesso.')
    setChaveAssinatura({ chave: payload.data?.salvarSistemaExterno.chaveAssinatura })
    setIsModalChaveAssinaturaOpen(true)
    setTimeout(() => {
      form.reset()
      form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
    })
  }

  const handleSubmit = (values: SistemaExternoFormModel, form: FormApi<SistemaExternoFormModel>) => {
    if (!isFormEnabled) {
      return
    }

    const onConfirm = async () => {
      setIsModalTermoOpen(false)
      const { localAcesso, ...input } = values

      salvarSistemaExterno({
        variables: { input },
      })
        .then((result) => {
          onSubmitSuccess(result, form)
        })
        .catch(handleRejection)
    }

    setTermoConfirmCallback(() => onConfirm)
    setIsModalTermoOpen(true)
  }

  const handleCloseChaveAssinaturaModal = () => {
    setIsModalChaveAssinaturaOpen(false)
    setChaveAssinatura(null)
  }

  const handleCloseTermoModal = () => {
    setIsModalTermoOpen(false)
  }

  const renderForm = (renderProps: FormRenderProps<Partial<SistemaExternoFormModel>>) => {
    const onReset = () => {
      renderProps.form.reset()
      renderProps.form.getRegisteredFields().forEach((field) => renderProps.form.resetFieldState(field))
    }

    const isPessoaFisica = renderProps.values.tipoPessoa === TipoPessoaEnum.FISICA

    return (
      <form noValidate>
        <Grid>
          <Cell size={4} alignSelf='flex-start'>
            <TextField name={path.nome} label='Nome do sistema' required />
          </Cell>
          <Cell size={8}>
            <Alert type='info' inline style={{ marginTop: '1.5rem', marginLeft: '-1rem' }}>
              Este nome será visível por todos os profissionais com acesso ao sistema externo.
            </Alert>
          </Cell>
          <Cell size={4} alignSelf='flex-start'>
            <SelectField
              name={path.localAcesso}
              label='Local de acesso'
              required
              items={['ATENDIMENTO']}
              itemToString={(item) => (item === 'ATENDIMENTO' ? 'Atendimento' : '')}
              defaultValue='ATENDIMENTO'
              disabled
            />
          </Cell>
          <Cell size={8} />
          <Cell size={12} alignSelf='flex-start'>
            <TextField name={path.url} label='URL' required />
          </Cell>
          <Cell size={12} alignSelf='flex-start'>
            <Alert type='info' inline style={{ width: '100%' }}>
              Os termos [documentoCidadao] e [documentoProfissional] quando escritos entre colchetes, serão substituídos
              pelos dados dentro do contexto do atendimento.
            </Alert>
          </Cell>
          <Cell size={12}>
            <TipoPessoaGroupField name={path.tipoPessoa} required label='Cadastrar sistema para' />
          </Cell>
          <Cell size={4}>
            <TextField name={path.nomeResponsavel} label='Responsável' required />
          </Cell>
          <Cell size={3}>
            {isPessoaFisica ? (
              <CpfField name={path.cpfCnpj} label='CPF' required />
            ) : (
              <CnpjField name={path.cpfCnpj} label='CNPJ' required />
            )}
          </Cell>
          <Cell size={5} />
          <Cell size={4}>
            <TextField name={path.email} label='E-mail' required />
          </Cell>
          <Cell size={8} />
          <Cell size={12}>
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={onReset}>
                Limpar campos
              </Button>

              <Tooltip
                text={
                  !isHttpsConnection &&
                  'Para cadastrar sistema externo é necessário que a instalação possua HTTPS configurado.'
                }
              >
                <SubmitButton size='small' handleSubmit={renderProps.handleSubmit} disabled={!isHttpsConnection}>
                  Cadastrar sistema
                </SubmitButton>
              </Tooltip>
            </HFlow>
          </Cell>
        </Grid>
      </form>
    )
  }

  return (
    <div css={styles.box}>
      <ChaveAssinaturaGeradaModal
        chaveAssinatura={chaveAssinatura}
        isOpen={isModalChaveAssinaturaOpen}
        onClose={handleCloseChaveAssinaturaModal}
      />
      <SistemasExternosTermoModal
        isOpen={isModalTermoOpen}
        onClose={handleCloseTermoModal}
        onAccept={termoConfirmCallback}
      />
      <Form
        render={renderForm}
        validate={validator}
        onSubmit={handleSubmit}
        initialValues={{ localAcesso: 'ATENDIMENTO', tipoPessoa: null }}
      />
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 1rem;
    border: 1px solid ${theme.pallete.divider};
  `,
})
